import { QuestionCircleOutlined } from '@ant-design/icons';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { Popover, Space } from 'antd';
import { type ReactNode, useState } from 'react';

export interface ConfirmProps {
    /** Confirmation title */
    title: ReactNode;
    /** Optional description below the title */
    description?: ReactNode;
    /** OK button text */
    okText?: string;
    /** Cancel button text */
    cancelText?: string;
    /** Use danger styling for OK button */
    okDanger?: boolean;
    /** Handler called when OK is clicked. Can be async. */
    onOk?: () => void | Promise<void>;
    /** Handler called when Cancel is clicked */
    onCancel?: () => void;
    /** Custom icon (defaults to QuestionCircleOutlined) */
    icon?: ReactNode;
    /** Whether the confirm is disabled */
    disabled?: boolean;
    /** The trigger element */
    children: ReactNode;
    /** Popover placement */
    placement?:
        | 'top'
        | 'left'
        | 'right'
        | 'bottom'
        | 'topLeft'
        | 'topRight'
        | 'bottomLeft'
        | 'bottomRight'
        | 'leftTop'
        | 'leftBottom'
        | 'rightTop'
        | 'rightBottom';
    /** Whether to stop click propagation when popup is clicked */
    stopPropagation?: boolean;
}

/**
 * Confirmation popover with ButtonModern buttons.
 * Drop-in replacement for Antd Popconfirm with custom button styling.
 *
 * @example
 * <Confirm
 *   title="Delete this item?"
 *   description="This action cannot be undone."
 *   onOk={handleDelete}
 *   okText="Delete"
 *   okDanger
 * >
 *   <Button.Icon icon={<Trash2 />} color="red" />
 * </Confirm>
 *
 * @example
 * <Confirm
 *   title="Are you sure?"
 *   onOk={handleConfirm}
 * >
 *   <Button type="primary">Proceed</Button>
 * </Confirm>
 */
export function Confirm({
    title,
    description,
    okText = 'OK',
    cancelText = 'Cancel',
    okDanger = false,
    onOk,
    onCancel,
    icon,
    disabled = false,
    children,
    placement = 'top',
    stopPropagation = true,
}: ConfirmProps) {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleOpenChange = (newOpen: boolean) => {
        if (disabled) return;
        setOpen(newOpen);
    };

    const handleOk = async (e: React.MouseEvent) => {
        if (stopPropagation) {
            e.stopPropagation();
        }
        if (onOk) {
            setLoading(true);
            try {
                await onOk();
            } finally {
                setLoading(false);
                setOpen(false);
            }
        } else {
            setOpen(false);
        }
    };

    const handleCancel = (e: React.MouseEvent) => {
        if (stopPropagation) {
            e.stopPropagation();
        }
        onCancel?.();
        setOpen(false);
    };

    const handlePopupClick = (e: React.MouseEvent) => {
        if (stopPropagation) {
            e.stopPropagation();
        }
    };

    const content = (
        <div onClick={handlePopupClick} className="max-w-64">
            <div className="mb-2 flex items-center gap-2">
                <span className="flex-shrink-0 text-warning">{icon ?? <QuestionCircleOutlined />}</span>
                <div>
                    <div className="font-medium text-foreground">{title}</div>
                    {description && <div className="mt-0.5 text-muted-foreground text-sm">{description}</div>}
                </div>
            </div>
            <Space className="flex justify-end" size="small">
                <Button type="secondary" size="small" onClick={handleCancel} disabled={loading}>
                    {cancelText}
                </Button>
                <Button type={okDanger ? 'danger-filled' : 'primary'} size="small" onClick={handleOk} loading={loading}>
                    {okText}
                </Button>
            </Space>
        </div>
    );

    return (
        <Popover
            content={content}
            trigger="click"
            open={open}
            onOpenChange={handleOpenChange}
            placement={placement}
            arrow={{ pointAtCenter: true }}
        >
            {children}
        </Popover>
    );
}
